import React from 'react';
import { useTranslation } from 'react-i18next';

export const SCHEDULE_TIMEZONE = 'America/New_York';

export const scheduleConfiguration = schedule => ({
  name: schedule.name,
  frequency_minutes: schedule.frequency_minutes,
  weekdays: [...schedule.weekdays],
  timezone: SCHEDULE_TIMEZONE,
  window_start: schedule.window_start,
  window_end: schedule.window_end,
  enabled: schedule.enabled,
});

export const isScheduleConfigurationValid = configuration => (
  configuration.name.trim() && configuration.timezone.trim() && configuration.weekdays.length > 0 &&
  Number.isInteger(configuration.frequency_minutes) && configuration.frequency_minutes >= 1 &&
  configuration.frequency_minutes <= 10080 && configuration.window_start && configuration.window_end &&
  configuration.window_start <= configuration.window_end
);

export default function ScheduleConfigurationFields({ configuration, onChange, disabled = false, showEnabled = true }) {
  const { t, i18n } = useTranslation();
  const inputClass = 'w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg';
  const timingInputClass = 'w-full min-w-0 px-1 py-2 border border-gray-300 rounded-lg text-xs';
  const change = (field, value) => onChange({ ...configuration, [field]: value });
  return (
    <fieldset disabled={disabled} className="space-y-4 min-w-0">
      <div className="grid grid-cols-2 items-end gap-2">
        <label className="block min-w-0 text-sm font-medium">
          {t('schedule_name', { defaultValue: 'Schedule name' })}
          <input className={inputClass} value={configuration.name} maxLength={100} required onChange={event => change('name', event.target.value)} />
        </label>
        <label className="block min-w-0 text-sm font-medium">
          {t('schedule_frequency', { defaultValue: 'Run every (minutes)' })}
          <input className={inputClass} type="number" min="1" max="10080" required value={configuration.frequency_minutes} onChange={event => change('frequency_minutes', Number(event.target.value))} />
        </label>
      </div>
      <div className="grid grid-cols-2 items-end gap-2">
        {['window_start', 'window_end'].map(field => (
          <label key={field} className="min-w-0 text-sm font-medium">
            {t(`schedule_${field}`, { defaultValue: field === 'window_start' ? 'From' : 'Until' })} (NY)
            <input className={timingInputClass} type="time" required value={configuration[field]} onChange={event => change(field, event.target.value)} />
          </label>
        ))}
      </div>
      <div className={showEnabled ? 'grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3' : undefined}>
        {showEnabled && <label className="block whitespace-nowrap text-sm font-medium">
          <span className="mb-2 block">{t('schedule_enabled', { defaultValue: 'Enabled' })}</span>
          <span className="flex h-6 items-center">
            <input type="checkbox" role="switch" aria-checked={configuration.enabled} name="enabled" checked={configuration.enabled} onChange={event => change('enabled', event.target.checked)} className="peer sr-only" />
            <span aria-hidden="true" className="relative h-6 w-11 shrink-0 cursor-pointer rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-blue-600 peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-50" />
          </span>
        </label>}
        <fieldset className="min-w-0">
          <legend className="text-sm font-medium mb-2">{t('schedule_days', { defaultValue: 'Days' })}</legend>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 7 }, (_, day) => (
              <label key={day} className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={configuration.weekdays.includes(day)} onChange={event => change('weekdays', event.target.checked ? [...configuration.weekdays, day].sort() : configuration.weekdays.filter(value => value !== day))} />
                {new Intl.DateTimeFormat(i18n?.language, { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, day + 1)))}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    </fieldset>
  );
}
